import { useEffect, useRef, useState } from "react";

interface SearchableOption {
    _id: string;
    name: string;
}

interface SearchableDropdownProps {
    value: string;
    options: SearchableOption[];
    onChange: (id: string) => void;
    excludeIds: string[];
    placeholder?: string;
    noResultsText?: string;
    variant?: 'default' | 'floating-label' | 'dark';
    hasError?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

export default function SearchableDropdown({ 
    value, 
    options, 
    onChange, 
    excludeIds, 
    placeholder = "Search...",
    noResultsText = "No results found",
    variant = 'default',
    hasError = false,
    onOpenChange
}: SearchableDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(option => option._id === value);
    const filteredOptions = options
        .filter(option => 
            !excludeIds.includes(option._id) &&
            option.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    if (variant === 'dark') {
        return (
            <div className="relative flex-1" ref={dropdownRef}>
                <div
                    className="relative w-full rounded-lg flex items-center cursor-pointer"
                    onClick={() => setIsOpen(true)}
                >
                    <input
                        type="text"
                        value={isOpen ? searchTerm : selectedOption?.name || ''}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => {
                            setIsOpen(true);
                            setSearchTerm('');
                        }}
                        placeholder={placeholder}
                        className="w-full rounded-lg border-0 bg-white/15 text-white placeholder-gray-400 px-3 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer backdrop-blur-sm text-base md:text-sm"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className={`h-4 w-4 text-gray-300 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>

                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-100 max-h-[220px] overflow-auto">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">{noResultsText}</div>
                        ) : (
                            filteredOptions.map(option => (
                                <div
                                    key={option._id}
                                    className={`px-3 py-2.5 cursor-pointer text-sm transition-colors ${option._id === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-800 hover:bg-gray-50'}`}
                                    onClick={() => {
                                        onChange(option._id);
                                        setIsOpen(false);
                                        setSearchTerm('');
                                    }}
                                >
                                    {option.name}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (variant === 'floating-label') {
        const inputClasses = `block w-full px-4 py-3 border rounded-md focus:outline-none transition-all duration-200 peer cursor-pointer pr-10 text-base md:text-sm ${
            hasError 
                ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500' 
                : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
        }`;
        
        return (
            <div className="relative flex-1" ref={dropdownRef}>
                <div
                    className="relative w-full rounded-md flex items-center"
                    onClick={() => setIsOpen(true)}
                >
                    <input
                        type="text"
                        value={isOpen ? searchTerm : selectedOption?.name || ''}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => {
                            setIsOpen(true);
                            setSearchTerm('');
                        }}
                        placeholder=" "
                        className={inputClasses}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>
                
                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-[200px] overflow-auto">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">{noResultsText}</div>
                        ) : (
                            filteredOptions.map(option => (
                                <div
                                    key={option._id}
                                    className="px-3 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                                    onClick={() => {
                                        onChange(option._id);
                                        setIsOpen(false);
                                        setSearchTerm('');
                                    }}
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <span className="text-gray-900 cursor-pointer">{option.name}</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Default variant
    return (
        <div className="relative flex-1" ref={dropdownRef}>
            <div
                className="relative w-full rounded-md border border-gray-300 shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 cursor-pointer hover:border-gray-400 transition-colors h-10 flex items-center"
                onClick={() => setIsOpen(true)}
            >
                <input
                    type="text"
                    value={isOpen ? searchTerm : selectedOption?.name || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => {
                        setIsOpen(true);
                        setSearchTerm('');
                    }}
                    placeholder={placeholder}
                    className="w-[calc(100%-2rem)] h-full px-3 rounded-md focus:outline-none cursor-pointer text-base md:text-sm"
                />
                <div className="flex items-center pr-2">
                    <svg className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>
            
            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-[200px] overflow-auto">
                    {filteredOptions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">{noResultsText}</div>
                    ) : (
                        filteredOptions.map(option => (
                            <div
                                key={option._id}
                                className="px-3 py-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors"
                                onClick={() => {
                                    onChange(option._id);
                                    setIsOpen(false);
                                    setSearchTerm('');
                                }}
                            >
                                <div className="flex items-center justify-between w-full">
                                    <span className="text-gray-900 cursor-pointer">{option.name}</span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}